// src/components/AddPatientModal.tsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getAvailableDepartments } from '../utils/departmentFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (patient: PatientFormData) => Promise<void>;
  onAddByMRN: (patient: PatientFormData) => void;
  defaultDepartment?: string;
  loggedInUserName: string;
  loggedInUserEmail: string;
}

export interface PatientFormData {
  MRN: string;
  Name: string;
  DOB: string;
  Department: string;
  Room: string;
  Diagnosis: string;
  Attending: string;
}

type TabType = 'add-new' | 'add-by-mrn';

const AddPatientModal: React.FC<AddPatientModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onAddByMRN,
  defaultDepartment = '',
  loggedInUserName,
  loggedInUserEmail
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('add-new');
  const [formData, setFormData] = useState<PatientFormData>({
    MRN: '',
    Name: '',
    DOB: '',
    Department: defaultDepartment,
    Room: '',
    Diagnosis: '',
    Attending: ''
  });
  const [mrnLookup, setMrnLookup] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mrnError, setMrnError] = useState<string | null>(null);
  const [mrnLookupError, setMrnLookupError] = useState<string | null>(null);

  // Update department when defaultDepartment changes or modal opens
  useEffect(() => {
    if (isOpen && defaultDepartment) {
      setFormData(prev => ({ ...prev, Department: defaultDepartment }));
    }
  }, [isOpen, defaultDepartment]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('add-new');
      setMrnLookup('');
      setError(null);
      setMrnError(null);
      setMrnLookupError(null);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(formData);
      setFormData({ MRN: '', Name: '', DOB: '', Department: '', Room: '', Diagnosis: '', Attending: '' });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add patient');
    } finally {
      setLoading(false);
    }
  };

  const handleAddByMRN = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mrnLookup.trim()) {
      setError('Please enter an MRN');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/patients/add-by-mrn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          MRN: mrnLookup.trim(),
          added_by: loggedInUserName,
          email: loggedInUserEmail
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onAddByMRN(data.patient);
        setMrnLookup('');
        onClose();
      } else {
        setError(data.message || 'Failed to add patient');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add patient');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Use Portal to render modal at document body level (fixes Safari/iOS clipping issues)
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content add-patient-modal">
        <div className="modal-header">
          <h2>Add Patient</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="add-patient-tabs">
          <button 
            type="button"
            className={`tab-btn ${activeTab === 'add-by-mrn' ? 'active' : ''}`}
            onClick={() => { setActiveTab('add-by-mrn'); setError(null); }}
          >
            Add by MRN
          </button>
          <button 
            type="button"
            className={`tab-btn ${activeTab === 'add-new' ? 'active' : ''}`}
            onClick={() => { setActiveTab('add-new'); setError(null); }}
          >
            Add New Patient
          </button>
        </div>
        
        {error && <div className="form-error">{error}</div>}
        
        {/* Add by MRN Tab */}
        {activeTab === 'add-by-mrn' && (
          <form onSubmit={handleAddByMRN} className="patient-form mrn-lookup-form">
            <div className="mrn-lookup-description">
              <p>Enter the MRN of an existing patient to add them to your list.</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="mrnLookup">MRN (Medical Record Number) *</label>
              <input
                type="text"
                id="mrnLookup"
                value={mrnLookup}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/\D/.test(value)) {
                    setMrnLookupError('MRN must contain only numbers');
                  } else {
                    setMrnLookupError(null);
                  }
                  setMrnLookup(value.replace(/\D/g, ''));
                  setError(null);
                }}
                placeholder="e.g., 1234"
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {mrnLookupError && <span className="field-error">{mrnLookupError}</span>}
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={loading || !mrnLookup.trim()}>
                {loading ? 'Looking up...' : 'Add to My List'}
              </button>
            </div>
          </form>
        )}
        
        {/* Add New Patient Tab */}
        {activeTab === 'add-new' && (
          <form onSubmit={handleSubmit} className="patient-form">
            <div className="form-group">
              <label htmlFor="MRN">MRN (Medical Record Number) *</label>
              <input
                type="text"
                id="MRN"
                name="MRN"
                value={formData.MRN}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/\D/.test(value)) {
                    setMrnError('MRN must contain only numbers');
                  } else {
                    setMrnError(null);
                  }
                  const numericValue = value.replace(/\D/g, '');
                  setFormData(prev => ({ ...prev, MRN: numericValue }));
                  setError(null);
                }}
                placeholder="e.g., 1234"
                required
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {mrnError && <span className="field-error">{mrnError}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="Name">Patient Name *</label>
              <input
                type="text"
                id="Name"
                name="Name"
                value={formData.Name}
                onChange={handleChange}
                placeholder="Full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="DOB">Date of Birth *</label>
              <input
                type="date"
                id="DOB"
                name="DOB"
                value={formData.DOB}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="Department">Department *</label>
              <select
                id="Department"
                name="Department"
                value={formData.Department}
                onChange={handleChange}
                required
              >
                <option value="" disabled={formData.Department !== ''}>Select Department</option>
                {getAvailableDepartments(loggedInUserEmail).map((dept: string) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="Room">Room # *</label>
              <input
                type="text"
                id="Room"
                name="Room"
                value={formData.Room}
                onChange={handleChange}
                placeholder="e.g., 302-A"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="Attending">Attending Physician *</label>
              <input
                type="text"
                id="Attending"
                name="Attending"
                value={formData.Attending}
                onChange={handleChange}
                placeholder="e.g., Dr. Sarah Williams"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="Diagnosis">Diagnosis *</label>
              <textarea
                id="Diagnosis"
                name="Diagnosis"
                value={formData.Diagnosis}
                onChange={handleChange}
                placeholder="Primary diagnosis"
                rows={3}
                required
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Patient'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AddPatientModal;
